/**
 * Thin wrapper over the Google Calendar v3 REST API.
 *
 * Nothing above this layer knows the Calendar API exists. The dashboard is the
 * source of truth and the sync is one-way: this module is only ever told what
 * the calendar should look like.
 *
 * Events are all-day on the deadline date, Tomato red, with reminders off. All
 * events being one colour is why the title has to carry the project name.
 */
import { config } from "../config";
import { eventBody, type CalendarEvent } from "../data/calendarPlan";

const BASE = "https://www.googleapis.com/calendar/v3";

export type { CalendarEvent };

export class CalendarError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly detail?: unknown
  ) {
    super(message);
    this.name = "CalendarError";
  }
}

function describe(status: number): string {
  switch (status) {
    case 401:
      return "Your Google session expired. Connect again to continue.";
    case 403:
      return "Calendar access was not granted. Disconnect and connect again to allow it.";
    case 404:
      return "The calendar or event was not found.";
    case 429:
      return "Google rate-limited the calendar. Deadlines will re-sync on the next load.";
    default:
      return `The calendar request failed (${status}).`;
  }
}

async function request<T>(
  path: string,
  token: string,
  init: RequestInit = {}
): Promise<T> {
  const response = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(init.headers ?? {}),
    },
  });
  if (!response.ok) {
    const detail = await response.json().catch(() => undefined);
    throw new CalendarError(describe(response.status), response.status, detail);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export const calendar = {
  /**
   * The id of the dedicated calendar, created on first use.
   *
   * A dedicated calendar rather than the primary one, so it can be toggled off
   * and so a bad sync can never touch real appointments.
   */
  async ensure(token: string): Promise<string> {
    const list = await request<{
      items?: { id?: string; summary?: string }[];
    }>("/users/me/calendarList?minAccessRole=owner&maxResults=250", token);

    const existing = (list.items ?? []).find(
      (c) => c.summary === config.calendarName
    );
    if (existing?.id) return existing.id;

    const created = await request<{ id?: string }>("/calendars", token, {
      method: "POST",
      body: JSON.stringify({
        summary: config.calendarName,
        description:
          "Deadlines from the task dashboard. One-way: the dashboard is the " +
          "source of truth, and edits made here are reverted on its next load.",
      }),
    });
    if (!created.id) throw new CalendarError("Could not create the calendar.", 500);
    return created.id;
  },

  /** Every all-day event on the calendar, in one call. */
  async list(calendarId: string, token: string): Promise<CalendarEvent[]> {
    const out: CalendarEvent[] = [];
    let pageToken: string | undefined;
    do {
      const page = await request<{
        items?: { id?: string; summary?: string; start?: { date?: string } }[];
        nextPageToken?: string;
      }>(
        `/calendars/${encodeURIComponent(calendarId)}/events` +
          `?singleEvents=true&showDeleted=false&maxResults=2500` +
          (pageToken ? `&pageToken=${encodeURIComponent(pageToken)}` : ""),
        token
      );
      for (const item of page.items ?? []) {
        if (item.id && item.start?.date) {
          out.push({ id: item.id, summary: item.summary ?? "", date: item.start.date });
        }
      }
      pageToken = page.nextPageToken;
    } while (pageToken);
    return out;
  },

  async create(
    calendarId: string,
    summary: string,
    date: string,
    token: string
  ): Promise<string> {
    const created = await request<{ id?: string }>(
      `/calendars/${encodeURIComponent(calendarId)}/events`,
      token,
      { method: "POST", body: JSON.stringify(eventBody(summary, date)) }
    );
    if (!created.id) throw new CalendarError("The event was not created.", 500);
    return created.id;
  },

  async update(
    calendarId: string,
    eventId: string,
    summary: string,
    date: string,
    token: string
  ): Promise<void> {
    await request(
      `/calendars/${encodeURIComponent(calendarId)}/events/${encodeURIComponent(eventId)}`,
      token,
      { method: "PATCH", body: JSON.stringify(eventBody(summary, date)) }
    );
  },

  /** Deleting an event that is already gone is not an error here. */
  async remove(calendarId: string, eventId: string, token: string): Promise<void> {
    try {
      await request(
        `/calendars/${encodeURIComponent(calendarId)}/events/${encodeURIComponent(eventId)}`,
        token,
        { method: "DELETE" }
      );
    } catch (cause) {
      if (cause instanceof CalendarError && (cause.status === 404 || cause.status === 410)) {
        return;
      }
      throw cause;
    }
  },
};
